import type { DeliverableType } from '../types/portal'

/**
 * WHO CAN ASK FOR A CHANGE, AND WHAT THE BOX SAYS — 29 Sep 2026.
 *
 * Tom, looking at the change box sitting under a podcast:
 *
 *   "Only audio and video can request changes. All others don't. And the
 *    video and audio should have a transcript to make the changes with
 *    timecodes — the client should not have to be made to work for all
 *    this."
 *
 * Both halves matter and they are the same decision:
 *
 * 1. **A change request only makes sense against a cut.** A photography set
 *    is a folder of frames, a document is a document, a graphic is a
 *    graphic — asking a client to describe a change to one in a free-text
 *    box produces a note nobody can act on. Those types show no change box
 *    at all now. They can still be approved.
 *
 * 2. **A cut always comes with its transcript.** The client clicks the line
 *    they mean and the timecode rides along on the request, so the studio
 *    knows exactly where. That is the whole point: the client should not
 *    have to hunt for a number and type it out. The one prompt below is
 *    written for people who have that transcript in front of them.
 *
 * An earlier pass here gave each medium its own sentence — audio, photos,
 * documents. That was solving the wrong problem: the fix was not better
 * wording for the types that cannot use the box, it was not showing them
 * the box.
 */

/** Video and Audio only. Everything else can be approved, not amended. */
export function canRequestChanges(type: DeliverableType): boolean {
  return type === 'Video' || type === 'Audio'
}

/**
 * One sentence, for the only two types that see it. It points at the
 * transcript first because that is the effortless route, and still accepts
 * plain words from anyone who would rather just say it.
 */
export const CHANGE_PROMPT =
  'Click a line in the transcript, or just say when — “around six minutes, the music under the intro…” is perfectly clear'
